"use client";

import { BAD_IMAGE_URL, MODAL_HEIGHT, MODAL_WIDTH } from "@/constants";
import { analytics } from "@analytics";
import { Box, ConfirmationDialog, Heading } from "@biom3/react";
import { useCallback, useEffect, useMemo, useState } from "react";

type AcceptOfferStatusModalProps = {
  state?: "SUCCESS" | "ERROR" | "LOADING";
  errorMessage?: string;
  successMessage?: string;
  onRetry?: () => void;
  onClose?: () => void;
  itemName?: string;
  itemImage?: string;
  offerPrice?: string;
  onGoToMarketplace?: () => void;
};

type ImageWithOverlayProps = {
  src: string;
  alt: string;
  overlaySrc?: string;
  width?: string;
  height?: string;
  mt?: string;
  mb?: string;
  overlayWidth?: string;
  overlayHeight?: string;
  overlayTop?: string;
  overlayLeft?: string;
  overlayTransform?: string;
};

const overlayImage = "/images/listingErrorModal.svg";
const successImage = "/images/acceptOfferSuccess.svg";
const defaultImage =
  "https://biome.immutable.com/hosted-assets/images/cloudImageDefaultImage--onDark.svg";

export const ImageWithOverlay = ({
  src,
  alt,
  overlaySrc,
  width,
  height,
  mt,
  mb,
  overlayWidth,
  overlayHeight,
  overlayTop,
  overlayLeft,
  overlayTransform,
}: ImageWithOverlayProps) => {
  const [mainImageSrc, setMainImageSrc] = useState<string>(src);

  useEffect(() => {
    setMainImageSrc(src);
  }, [src]);

  return (
    <Box
      sx={{
        d: "flex",
        justifyContent: "center",
        position: "relative",
        width: width ?? "128px",
        height: height ?? "128px",
        mt: mt,
        mb: mb || "base.spacing.x3",
      }}
    >
      <img
        src={mainImageSrc}
        alt={alt}
        width={width}
        height={height}
        style={{ borderRadius: 8 }}
        onError={() => {
          setMainImageSrc(defaultImage);
        }}
      />
      {overlaySrc && (
        <img
          src={overlaySrc}
          alt="Overlay"
          style={{
            position: "absolute",
            top: overlayTop ?? 55,
            left: overlayLeft ?? 90,
            width: overlayWidth ?? 77,
            height: overlayHeight ?? 95,
            transform: overlayTransform ?? "0",
          }}
        />
      )}
    </Box>
  );
};

export const AcceptOfferStatusModal = ({
  state,
  onRetry,
  onClose,
  itemName,
  itemImage,
  offerPrice,
  onGoToMarketplace,
}: AcceptOfferStatusModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const open = state !== undefined;
    setIsModalOpen(open);

    if (open) {
      let control: string;
      switch (state) {
        case "SUCCESS":
          analytics.track(
            "PassportDashboardBidAcceptOfferStatusModal_AcceptOfferSuccessEffect",
          );
          break;
        case "ERROR":
          analytics.track(
            "PassportDashboardBidAcceptOfferStatusModal_AcceptOfferFailureEffect",
          );
          break;
        default:
          analytics.track(
            "PassportDashboardBidAcceptOfferStatusModal_AcceptOfferLoadingEffect",
          );
          break;
      }
    }
  }, [state]);

  const handleClose = useCallback(() => {
    analytics.track(
      "PassportDashboardBidAcceptOfferStatusModal_CloseButtonPressed",
    );

    if (onClose) onClose();
    setIsModalOpen(false);
  }, [onClose]);

  const handleRetry = useCallback(() => {
    analytics.track(
      "PassportDashboardBidAcceptOfferStatusModal_RetryButtonPressed",
    );

    if (onRetry) onRetry();
  }, [onRetry]);

  const handleGoToMarketplace = useCallback(() => {
    analytics.track(
      "PassportDashboardBidAcceptOfferStatusModal_GoToMarketplaceButtonPressed",
    );

    if (onGoToMarketplace) onGoToMarketplace();
    setIsModalOpen(false);
  }, [onGoToMarketplace]);

  const modalContent = useMemo(() => {
    switch (state) {
      case "SUCCESS":
        return {
          title: `Sold ${itemName}`,
          offerPrice: offerPrice,
          description:
            "You can continue to buy items for your inventory in our marketplaces.",
          imageSrc: successImage,
          primaryButton: {
            text: "Explore Marketplaces",
            action: handleGoToMarketplace,
          },
          secondaryButton: {
            text: "Close",
            action: handleClose,
          },
          imageWidth: "430px",
          imageHeight: "240px",
        };
      case "ERROR":
        return {
          title: `We could not sell ${itemName}`,
          description:
            "Something went wrong, please try again later, or contact us if the error persists.",
          imageSrc: itemImage,
          primaryButton: onRetry
            ? {
                text: "Try Again",
                action: handleRetry,
              }
            : undefined,
          secondaryButton: {
            text: "Close",
            action: handleClose,
          },
          mt: "base.spacing.x20",
        };
      case "LOADING":
        return {
          imageSrc: itemImage,
          title: "Selling your item...",
          mt: "base.spacing.x20",
        };
      default:
        return undefined;
    }
  }, [
    state,
    itemName,
    itemImage,
    handleClose,
    handleRetry,
    onRetry,
    handleGoToMarketplace,
    offerPrice,
  ]);

  if (!modalContent) return null;

  return (
    <ConfirmationDialog visible={isModalOpen} outsideClicksClose={false}>
      <ConfirmationDialog.Content
        sx={{
          d: "flex",
          width: MODAL_WIDTH,
          height: MODAL_HEIGHT,
          alignItems: "center",
          background: "base.color.neutral.900",
        }}
      >
        <ConfirmationDialog.Content.Image
          alt={modalContent.title}
          use={
            <ImageWithOverlay
              src={modalContent.imageSrc ?? BAD_IMAGE_URL}
              alt={modalContent.title}
              overlaySrc={state === "ERROR" ? overlayImage : undefined}
              width={modalContent.imageWidth ?? "128px"}
              height={modalContent.imageHeight ?? "128px"}
              mt={modalContent.mt}
            />
          }
        />
        <ConfirmationDialog.Content.Title sx={{ px: "base.spacing.x12" }}>
          {modalContent.title}
        </ConfirmationDialog.Content.Title>
        {modalContent.offerPrice && (
          <ConfirmationDialog.Content.Caption
            sx={{
              px: "base.spacing.x12",
              color: "base.color.text.heading.emphasis",
            }}
          >
            <Heading>{modalContent.offerPrice}</Heading>
          </ConfirmationDialog.Content.Caption>
        )}

        {modalContent.description && (
          <ConfirmationDialog.Content.Caption sx={{ px: "base.spacing.x12" }}>
            {modalContent.description}
          </ConfirmationDialog.Content.Caption>
        )}
        {modalContent.primaryButton && (
          <ConfirmationDialog.Content.Button
            size="large"
            variant="primary"
            onClick={modalContent.primaryButton.action}
          >
            {modalContent.primaryButton.text}
          </ConfirmationDialog.Content.Button>
        )}
        {modalContent.secondaryButton && (
          <ConfirmationDialog.Content.Button
            size="large"
            variant="tertiary"
            onClick={modalContent.secondaryButton.action}
          >
            {modalContent.secondaryButton.text}
          </ConfirmationDialog.Content.Button>
        )}
      </ConfirmationDialog.Content>
    </ConfirmationDialog>
  );
};
